import React from 'react';
import type { CrewMember, Document } from '../types';
import { getCertificateStatusFromDocuments, CertStatus } from '../utils/certificateUtils';

interface CrewTableProps {
  crew: CrewMember[];
  documents: Document[];
  raceId?: string;
  onRemoveCrew: (crewId: string) => void;
  onInviteCrew?: (crewId: string) => void;
  removingCrewId: string | null;
  invitingCrewId?: string | null;
}

const INVITE_COLORS: Record<string, string> = {
  invite: '#1a3a5c',
  sent: '#2196f3',
  accepted: '#4caf50',
  declined: '#f44336',
  reinvite: '#ff9800',
};

function getInviteDisplay(status?: string, sentAt?: string, hasPhone?: boolean): {
  label: string; color: string; clickable: boolean;
} {
  if (status === 'accepted') return { label: 'accepted', color: INVITE_COLORS.accepted, clickable: false };
  if (status === 'declined') return { label: 'declined', color: INVITE_COLORS.declined, clickable: false };
  if (status === 'sent') {
    if (sentAt) {
      const hours = (Date.now() - new Date(sentAt).getTime()) / 3_600_000;
      if (hours > 24) return { label: 're-invite', color: INVITE_COLORS.reinvite, clickable: true };
    }
    return { label: 'sent', color: INVITE_COLORS.sent, clickable: false };
  }
  if (hasPhone) return { label: 'invite', color: INVITE_COLORS.invite, clickable: true };
  return { label: '', color: '', clickable: false };
}

const CertIcon: React.FC<{ status: CertStatus }> = ({ status }) => {
  if (status === 'valid') return <span className="cert-tick" title="Valid certificate">✓</span>;
  if (status === 'expired') return <span className="cert-expired" title="Certificate expired">⚠️</span>;
  return null;
};

const CrewTable: React.FC<CrewTableProps> = ({
  crew, documents, onRemoveCrew, onInviteCrew, removingCrewId, invitingCrewId,
}) => {
  if (crew.length === 0) {
    return (
      <div className="crew-table-empty">
        <p>No crew members assigned to this event yet.</p>
        <p>Click "Add Crew" to add crew members from your master list.</p>
      </div>
    );
  }

  return (
    <table className="crew-cert-table">
      <thead>
        <tr>
          <th>Name</th>
          <th className="cert-col">SSSC</th>
          <th className="cert-col">First Aid</th>
          <th className="cert-col">Radio</th>
          <th className="actions-col">Actions</th>
        </tr>
      </thead>
      <tbody>
        {crew.map((member) => {
          const certStatus = getCertificateStatusFromDocuments(member.crewId, documents);
          const isRemoving = removingCrewId === member.crewId;
          const isInviting = invitingCrewId === member.crewId;
          const invite = getInviteDisplay(member.inviteStatus, member.inviteSentAt, !!member.phoneNumber);

          return (
            <tr key={member.crewId}>
              <td>
                {member.firstName} {member.lastName}
                {invite.label && (
                  invite.clickable && onInviteCrew ? (
                    <button
                      type="button"
                      onClick={() => onInviteCrew(member.crewId)}
                      disabled={isInviting}
                      style={{
                        backgroundColor: invite.color,
                        color: '#fff',
                        fontSize: '0.7rem',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        marginLeft: '8px',
                        border: 'none',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                      title={invite.label === 'invite' ? 'Send WhatsApp invite' : 'Re-send WhatsApp invite'}
                    >
                      {isInviting ? '...' : invite.label}
                    </button>
                  ) : (
                    <span
                      style={{
                        backgroundColor: invite.color,
                        color: '#fff',
                        fontSize: '0.7rem',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        marginLeft: '8px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {invite.label}
                    </span>
                  )
                )}
              </td>
              <td className="cert-col"><CertIcon status={certStatus.sssc} /></td>
              <td className="cert-col"><CertIcon status={certStatus.firstAid} /></td>
              <td className="cert-col"><CertIcon status={certStatus.radio} /></td>
              <td className="actions-col actions-inline">
                <button
                  className="btn-icon"
                  onClick={() => onRemoveCrew(member.crewId)}
                  disabled={isRemoving}
                  title="Remove from event"
                  aria-label={`Remove ${member.firstName} ${member.lastName} from event`}
                >
                  {isRemoving ? '...' : '🗑️'}
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default CrewTable;
